import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './BookingCompletePage.css';
import { trackPurchaseOnce } from '../lib/analytics';

interface ReservationInfo {
  id: string;
  property_id?: string;
  coupon_code?: string | null;
  access_code?: string | null;
  property_name?: string;
  checkin_date: string;
  checkout_date: string;
  guest_count: number;
  total_price: number;
  currency: string;
  guest_email: string;
  status: string;
  cancellation_policy?: {
    free_cancel_until_days: number;
    free_cancel_until: string;
    is_free_cancelable_now: boolean;
    note: string;
  };
}

function BookingCompletePage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const reservationId = params.get('reservation_id');
  const email = params.get('email'); // 任意（照会用）

  const [reservation, setReservation] = useState<ReservationInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const sessionId = params.get('session_id');

  useEffect(() => {
    if (!reservationId || !email) {
      // メールが無い場合は照会できないため、成功メッセージのみ表示
      setLoading(false);
      return;
    }
    const lookup = () =>
      fetch(
        `/api/reservations/lookup?reservation_id=${encodeURIComponent(
          reservationId
        )}&email=${encodeURIComponent(email)}`
      ).then((r) => (r.ok ? r.json() : null));

    (async () => {
      try {
        // Webhook が届かない場合に備え、Stripe の支払い状況を確認して確定させる
        if (sessionId && sessionId.startsWith('cs_')) {
          await fetch(`/api/reservations/${encodeURIComponent(reservationId)}/verify-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId }),
          }).catch(() => null);
        }
        const info: ReservationInfo | null = await lookup();
        setReservation(info);
        // GA4 purchase（H4）：支払い確定（CONFIRMED）のときだけ、予約IDごとに1回
        if (info && info.status === 'CONFIRMED' && info.property_id) {
          trackPurchaseOnce({
            reservationId: info.id,
            propertyId: info.property_id,
            propertyName: info.property_name,
            value: info.total_price,
            currency: info.currency || 'JPY',
            lang: i18n.language,
            coupon: info.coupon_code,
          });
        }
      } catch {
        setReservation(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [reservationId, email, sessionId]);

  const fmtMoney = (n: number, currency = 'JPY') =>
    new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
    }).format(n);

  return (
    <div className="complete-page">
      <div className="container complete-container">
        <div className="complete-card">
          <div className="check-circle" aria-hidden>
            ✓
          </div>
          <h1 className="complete-title">{t('confirmation.thankYou')}</h1>
          <p className="complete-sub">{t('confirmation.confirmEmailSent')}</p>

          {reservationId && (
            <div className="booking-number">
              <span className="bn-label">{t('confirmation.bookingNumber')}</span>
              <span className="bn-value">{reservationId}</span>
            </div>
          )}

          {loading ? (
            <p className="complete-loading">{t('common.loading')}</p>
          ) : reservation ? (
            <div className="detail-box">
              {reservation.property_name && (
                <div className="detail-prop">{reservation.property_name}</div>
              )}
              <div className="detail-row">
                <span>{t('confirmation.checkinLabel')}</span>
                <span>{reservation.checkin_date}</span>
              </div>
              <div className="detail-row">
                <span>{t('confirmation.checkoutLabel')}</span>
                <span>{reservation.checkout_date}</span>
              </div>
              <div className="detail-row">
                <span>{t('confirmation.guestCountLabel')}</span>
                <span>
                  {reservation.guest_count} {t('booking.guests')}
                </span>
              </div>
              <div className="detail-row total">
                <span>{t('confirmation.totalLabel')}</span>
                <span>
                  {fmtMoney(reservation.total_price, reservation.currency)}
                </span>
              </div>
              {reservation.cancellation_policy && (
                <div className="cancel-policy-note">
                  <strong>{t('confirmation.cancellationTitle')}</strong>
                  <p>
                    {reservation.cancellation_policy.free_cancel_until} {t('confirmation.freeCancelUntilSuffix')}
                  </p>
                </div>
              )}
            </div>
          ) : null}

          <Link to="/" className="btn btn-home">
            {t('confirmation.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default BookingCompletePage;
